/* eslint-disable sonarjs/cognitive-complexity */
import React, { FC, useMemo, useState, useEffect } from 'react'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/'
import { ReservationObject, TheatricalReleaseObject, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { BannerPlaceholder } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/BannerPlaceholder'
import ChangeReservationBanner from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/ChangeReservationBanner'
import { TicketPromoBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TicketPromoBanner'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface tlrPromosProps {
  mobile: string
  tablet: string
  desktop: string
  desktopOverlay?: React.ReactNode
  tabletOverlay?: React.ReactNode
  mobileOverlay?: React.ReactNode
}

export const tlrPromos: Record<string, tlrPromosProps> = {
  freeteen: {
    mobile: '/v1745714665/the-last-rodeo/lastrodeo-teenfree-banner-2x-mobile.webp',
    tablet: '/v1745714665/the-last-rodeo/lastrodeo-teenfree-banner-2x-tablet.webp',
    desktop: '/v1745714665/the-last-rodeo/lastrodeo-teenfree-banner-2x-desk.webp',
  },
  freekid: {
    mobile: '/v1745714664/the-last-rodeo/lastrodeo-buckaroo-banner-2x-desk.webp',
    tablet: '/v1745714664/the-last-rodeo/lastrodeo-buckaroo-banner-2x-desk.webp',
    desktop: '/v1745714664/the-last-rodeo/lastrodeo-buckaroo-banner-2x-desk.webp',
  },
}

const renderPromoBanner = (promoConfig: tlrPromosProps) => (
  <div className="relative mb-2 @container">
    <div className="relative hidden cursor-pointer lg:block">
      <Image className="h-full w-full rounded-xl" src={promoConfig.desktop} alt={''} width={2000} height={334} />
      {promoConfig.desktopOverlay}
    </div>
    <div className="relative hidden cursor-pointer md:block lg:hidden">
      <Image className="h-full w-full rounded-[10px]" src={promoConfig.tablet} alt={''} width={1200} height={300} />
      {promoConfig.tabletOverlay}
    </div>
    <div className="relative md:hidden">
      <Image src={promoConfig.mobile} alt={''} className="h-full w-full rounded-lg" width={856} height={286} />
      {promoConfig.mobileOverlay}
    </div>
  </div>
)

interface BannerSectionProps {
  reservation?: ReservationObject
  isEditReservationFlow: boolean
  theatricalRelease: TheatricalReleaseObject
  venues: TheatricalShowtimeVenue[]
}

const TicketBannerSection: FC<BannerSectionProps> = ({
  reservation,
  isEditReservationFlow,
  theatricalRelease,
  venues,
}) => {
  const { t } = useTranslate('tickets')
  const { isReady, query } = useSafeRouter()
  const { isFreeTicketPromo } = useTicketsContext()
  const countryCode = theatricalRelease?.region?.countryCode
  const { areGuildTicketsAvailable, loading: guildTicketsLoading } = useGuildTickets(theatricalRelease?.theatricalSlug)
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])

  const venuesWithShowtimes = useMemo(() => {
    return venues?.filter((venue) => venue?.showtimes?.length > 0)
  }, [venues])

  const queryTlrPromo = useMemo(() => {
    const queryPromo = getItemOrFirstEntry(query?.promo)
    if (!queryPromo || theatricalRelease?.theatricalSlug !== slugs.theLastRodeo) return null

    return tlrPromos[queryPromo.toLowerCase()]
  }, [query?.promo, theatricalRelease?.theatricalSlug])

  if (!isReady || venuesWithShowtimes?.length === 0) {
    return null
  }

  if (guildTicketsLoading && isClient) {
    return <BannerPlaceholder />
  }

  return (
    <>
      {!isEditReservationFlow && !isFreeTicketPromo && !queryTlrPromo && (
        <TicketPromoBanner
          theatricalSlug={theatricalRelease?.theatricalSlug}
          areGuildTicketsAvailable={areGuildTicketsAvailable}
          isGuildDataLoading={guildTicketsLoading}
          className="mb-4 rounded-xl !pr-0"
        />
      )}

      {!areGuildTicketsAvailable &&
        !isEditReservationFlow &&
        !isFreeTicketPromo &&
        queryTlrPromo &&
        renderPromoBanner(queryTlrPromo)}

      {reservation?.priorReservation?.id &&
        reservation?.isChangeReservation &&
        isAngelIntegratedCountry(countryCode) && (
          <ChangeReservationBanner
            description={t('pleaseSelectANewShowtimeBelow', 'Please select a new showtime below.')}
            priorReservation={reservation?.priorReservation}
            step={1}
          />
        )}
    </>
  )
}

export default TicketBannerSection
